import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import PageContainer from './PageContainer';
import SubscriptionBanner from './SubscriptionBanner';
import { useUserProfile } from '@/hooks/useUsersApi';
import { useAuthStore } from '@/hooks/useAuthStore';

interface LayoutProps {
  children: ReactNode;
}

const SIDEBAR_WIDTH = '16rem'; // 64 = w-64
const SIDEBAR_COLLAPSED_WIDTH = '5rem'; // 20 = w-20

const Layout = ({ children }: LayoutProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Sync user profile (firstName, lastName, phone) into the auth store so
  // components like UserMenu can display the user's name without extra fetches.
  const { data: profileData } = useUserProfile();
  const updateUser = useAuthStore((s) => s.updateUser);
  useEffect(() => {
    if (profileData) {
      updateUser({
        firstName: profileData.firstName ?? undefined,
        lastName: profileData.lastName ?? undefined,
        phone: profileData.phone ?? undefined,
      });
    }
  }, [profileData, updateUser]);

  const sidebarWidth = isSidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const handleMenuToggle = (isOpen: boolean) => {
    setIsMobileMenuOpen(isOpen);
  };

  const handleMobileClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleCollapseToggle = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const handleSearch = (_query: string) => {};

  const handleCommandPalette = () => {};

  const handleNotificationAction = (_action: string, _notificationId?: string) => {};

  const logout = useAuthStore((s) => s.logout);
  const handleUserAction = (action: string) => {
    if (action === 'logout') {
      logout();
      window.location.href = '/login';
    }
  };

  return (
    <div
      className="min-h-screen bg-secondary-50"
      style={{ '--sidebar-width': sidebarWidth } as React.CSSProperties}
    >
      {/* Header, full width, fixed top */}
      <Header
        onMenuToggle={handleMenuToggle}
        onSearch={handleSearch}
        onCommandPalette={handleCommandPalette}
        onNotificationAction={handleNotificationAction}
        onUserAction={handleUserAction}
      />

      {/* Main layout with sidebar and content */}
      <div className="flex pt-16 pb-14">
        {/* Sidebar, below header */}
        <Sidebar
          isMobileOpen={isMobileMenuOpen}
          onMobileClose={handleMobileClose}
          isCollapsed={isSidebarCollapsed}
          onCollapseToggle={handleCollapseToggle}
          onExpand={() => setIsSidebarCollapsed(false)}
        />

        {/* Main content area */}
        <main className="flex-1 overflow-y-auto min-h-[calc(100vh-4rem)]">
          <SubscriptionBanner />
          <PageContainer>{children}</PageContainer>
        </main>
      </div>

      {/* Footer, offset to right of sidebar */}
      <Footer sidebarWidth={sidebarWidth} />
    </div>
  );
};

export default Layout;
